// src/pages/RecruitmentAnalytics/RecruitmentLayout.tsx
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation, Outlet } from 'react-router-dom';
import {
  LayoutDashboard,
  Briefcase,
  Users,
  FileText,
  ChevronLeft,
  ChevronRight,
  ArrowLeft,
  TrendingUp,
  Globe,
  Clock,
} from 'lucide-react';
import ChatWidget from '../../components/ChatWidget';
import { Tooltip } from 'react-tooltip';
import { XCircle, Target } from 'lucide-react';

const RecruitmentLayout: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [scrollY, setScrollY] = useState(0);
  const handleScroll = useCallback(() => {
    requestAnimationFrame(() => setScrollY(window.scrollY));
  }, []);
  useEffect(() => {
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  const [collapsed, setCollapsed] = useState(() => {
    const saved = localStorage.getItem('recruitment-sidebar-collapsed');
    return saved !== null ? JSON.parse(saved) : false;
  });

  useEffect(() => {
    localStorage.setItem('recruitment-sidebar-collapsed', JSON.stringify(collapsed));
  }, [collapsed]);

  const menuItems = [
    { path: '/app/recruitment-analytics', icon: <LayoutDashboard size={20} />, label: 'Дашборд', exact: true },
    { path: '/app/recruitment-analytics/vacancies', icon: <Briefcase size={20} />, label: 'Вакансии' },
    { path: '/app/recruitment-analytics/candidates', icon: <Users size={20} />, label: 'Кандидаты' },
    { path: '/app/recruitment-analytics/requisitions', icon: <FileText size={20} />, label: 'Заявки' },
    { path: '/app/recruitment-analytics/predictive', icon: <TrendingUp size={20} />, label: 'Прогнозы и риски' },
    { path: '/app/recruitment-analytics/performance', icon: <Users size={20} />, label: 'Эффективность рекрутеров' },
   // { path: '/app/recruitment-analytics/labor-market', icon: <Globe size={20} />, label: 'Рынок труда' },
    { path: '/app/recruitment-analytics/vacancy-aging', icon: <Clock size={20} />, label: 'Возраст вакансий' },
   // { path: '/app/recruitment-analytics/rejection-analysis', icon: <XCircle size={20} />, label: 'Причины отказов'},
    { path: '/app/recruitment-analytics/plan-vs-actual', icon: <Target size={20} />, label: 'План/Факт'}
  ];

  const isActive = (path: string, exact?: boolean) => {
    if (exact) return location.pathname === path;
    return location.pathname.startsWith(path);
  };

  const sidebarWidth = collapsed ? 80 : 260;
  const panelOffset = 10;
  const buttonLeft = sidebarWidth + panelOffset - 12;

  const toggleButtonRef = useRef<HTMLButtonElement>(null);
  const backButtonRef = useRef<HTMLButtonElement>(null);

  const handleBackButtonMouseEnter = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.currentTarget.style.background = 'linear-gradient(135deg, rgba(106, 17, 203, 0.9), rgba(37, 117, 252, 0.9))';
    e.currentTarget.style.color = 'white';
    e.currentTarget.style.transform = 'scale(1.02)';
    e.currentTarget.style.border = '1px solid rgba(255,255,255,0.6)';
  };

  const handleBackButtonMouseLeave = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)';
    e.currentTarget.style.color = '#1a1a2e';
    e.currentTarget.style.transform = 'scale(1)';
    e.currentTarget.style.border = '1px solid rgba(255, 255, 255, 0.3)';
  };

  const handleToggleButtonMouseEnter = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.currentTarget.style.background = 'linear-gradient(135deg, rgba(106, 17, 203, 0.9), rgba(37, 117, 252, 0.9))';
    e.currentTarget.style.color = 'white';
    e.currentTarget.style.transform = 'scale(1.02)';
    e.currentTarget.style.border = '1px solid rgba(255,255,255,0.6)';
  };

  const handleToggleButtonMouseLeave = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.currentTarget.style.background = 'white';
    e.currentTarget.style.color = '#2575fc';
    e.currentTarget.style.transform = 'scale(1)';
    e.currentTarget.style.border = '1px solid #2575fc';
  };

  return (
    <div style={styles.container}>
      {/* Параллакс-пятна (как на лендинге) */}
      <div style={{
        position: 'fixed', borderRadius: '50%', zIndex: 0, pointerEvents: 'none',
        willChange: 'transform', transform: `translate3d(0, ${scrollY * 0.1}px, 0)`,
        background: 'radial-gradient(circle, rgba(106,17,203,0.18) 0%, transparent 70%)',
        top: '-10%', left: '-5%', width: '45vw', height: '45vw'
      }} />
      <div style={{
        position: 'fixed', borderRadius: '50%', zIndex: 0, pointerEvents: 'none',
        willChange: 'transform', transform: `translate3d(0, ${scrollY * -0.15}px, 0)`,
        background: 'radial-gradient(circle, rgba(37,117,252,0.15) 0%, transparent 70%)',
        bottom: '5%', right: '-10%', width: '55vw', height: '55vw'
      }} />

      {/* Боковая панель */}
      <div
        style={{
          ...styles.sidebar,
          width: sidebarWidth,
          left: panelOffset,
          top: panelOffset,
          bottom: panelOffset,
          borderRadius: 20,
          boxShadow: '0 20px 40px rgba(0,0,0,0.25), 0 8px 20px rgba(0,0,0,0.15)',
          background: 'rgba(106, 17, 203, 0.2)',
        }}
      >
        <div style={styles.logo}>
          <img src="/images/10.png" alt="LAMA" style={styles.logoIcon} />
          {!collapsed && <span style={styles.logoText}>Рекрутмент</span>}
        </div>

        <nav style={styles.nav}>
          {menuItems.map(item => (
            <button
              key={item.path}
              onClick={() => navigate(item.path)}
              className="recruitment-nav-item"
              style={{
                ...styles.navItem,
                ...(isActive(item.path, item.exact) ? styles.navItemActive : {}),
                justifyContent: collapsed ? 'center' : 'flex-start',
              }}
              data-tooltip-id={collapsed ? "recruitment-tooltip" : undefined}
              data-tooltip-content={collapsed ? item.label : undefined}
              data-tooltip-place="right"
            >
              {item.icon}
              {!collapsed && <span style={styles.navLabel}>{item.label}</span>}
            </button>
          ))}
        </nav>

        <div style={styles.footer}>
          <button
            ref={backButtonRef}
            onClick={() => navigate('/dashboard')}
            style={{
              ...styles.backButton,
              width: collapsed ? '36px' : '100%',
              height: collapsed ? '36px' : 'auto',
              borderRadius: collapsed ? '18px' : '30px',
              padding: collapsed ? '0' : '10px 16px',
              justifyContent: collapsed ? 'center' : 'flex-start',
            }}
            data-tooltip-id={collapsed ? "recruitment-tooltip" : undefined}
            data-tooltip-content={collapsed ? "На главный дашборд" : undefined}
            data-tooltip-place="right"
            onMouseEnter={handleBackButtonMouseEnter}
            onMouseLeave={handleBackButtonMouseLeave}
          >
            <ArrowLeft size={16} />
            {!collapsed && <span style={{ marginLeft: '8px' }}>На главный дашборд</span>}
          </button>
        </div>
      </div>

      {/* Кнопка сворачивания */}
      <button
        ref={toggleButtonRef}
        onClick={() => setCollapsed(!collapsed)}
        style={{ ...styles.toggleButton, left: buttonLeft }}
        onMouseEnter={handleToggleButtonMouseEnter}
        onMouseLeave={handleToggleButtonMouseLeave}
      >
        {collapsed ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
      </button>

      {/* Основной контент */}
      <div style={{ ...styles.content, marginLeft: sidebarWidth + panelOffset + 20 }}>
        <Outlet />
      </div>

      <ChatWidget />
      <Tooltip id="recruitment-tooltip" delayShow={0} delayHide={100} style={{ fontSize: '12px', backgroundColor: 'rgba(0,0,0,0.8)', borderRadius: '8px', padding: '4px 8px' }} />

      <style>{`
        .recruitment-nav-item {
          transition: background 0.2s, border-radius 0.2s;
          border-radius: 30px !important;
          margin: 4px 0;
        }
        .recruitment-nav-item:hover {
          background: rgba(37, 117, 252, 0.1) !important;
        }
        .recruitment-nav-item:active {
          background: rgba(37, 117, 252, 0.2) !important;
        }
      `}</style>
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  container: {
    minHeight: '100vh',
    position: 'relative',
    background: 'linear-gradient(135deg, #f5f7fa 0%, #e6f0fa 50%, #f0e6ff 100%)',
  },
  sidebar: {
    position: 'fixed',
    background: 'rgba(106, 17, 203, 0.2)',
    color: 'white',
    transition: 'width 0.3s, left 0.3s, bottom 0.3s, top 0.3s',
    zIndex: 1000,
    display: 'flex',
    flexDirection: 'column',
    border: '1px solid rgba(255,255,255,0.2)',
  },
  logo: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '20px 16px',
    borderBottom: '1px solid rgba(255,255,255,0.2)',
  },
  logoIcon: { height: '32px', width: 'auto' },
  logoText: {
    fontSize: '16px',
    fontWeight: 700,
    whiteSpace: 'nowrap',
    color: '#1a1a2e',
  },
  nav: { flex: 1, padding: '20px 0', overflowY: 'auto' },
  navItem: {
    width: '100%',
    padding: '12px 16px',
    background: 'transparent',
    border: 'none',
    color: '#1a1a2e',
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    cursor: 'pointer',
    fontSize: '14px',
    borderRadius: '30px',
    margin: '4px 0',
  },
  navItemActive: {
    background: 'rgba(37, 117, 252, 0.2)',
    color: '#2575fc',
    fontWeight: 600,
    borderRadius: '30px',
  },
  navLabel: { whiteSpace: 'nowrap' },
  footer: { padding: '20px 16px', borderTop: '1px solid rgba(255,255,255,0.2)' },
  backButton: {
    background: 'rgba(255, 255, 255, 0.2)',
    backdropFilter: 'blur(8px)',
    border: '1px solid rgba(255, 255, 255, 0.3)',
    color: '#1a1a2e',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    transition: 'all 0.2s ease',
    fontSize: '14px',
    fontWeight: 500,
  },
  toggleButton: {
    position: 'fixed',
    top: '60px',
    width: '24px',
    height: '24px',
    borderRadius: '12px',
    background: 'white',
    border: '1px solid #2575fc',
    color: '#2575fc',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1001,
    transition: 'left 0.3s',
  },
  content: {
    transition: 'margin-left 0.3s',
    minHeight: '100vh',
    paddingTop: '20px',
  },
};

export default RecruitmentLayout;